// HMAC-signed transient payload for preserving a share intent across a login redirect.
// The session key lives in sessionStorage so it cannot be predicted from a crafted URL;
// the signed payload prevents a malicious link from injecting text post-login.

const SK_ITEM = 'cs-share-sk'   // per-tab session signing key (base64 raw HMAC-SHA-256)
const PT_ITEM = 'cs-share-pt'   // pending signed token (JSON)
const TTL_MS  = 15 * 60 * 1000 // 15-minute window

async function getOrCreateSessionKey(): Promise<CryptoKey> {
  const stored = sessionStorage.getItem(SK_ITEM)
  if (stored) {
    const raw = Uint8Array.from(atob(stored), (c) => c.charCodeAt(0))
    return crypto.subtle.importKey(
      'raw', raw,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign', 'verify'],
    )
  }
  const key = await crypto.subtle.generateKey(
    { name: 'HMAC', hash: 'SHA-256' },
    true,
    ['sign', 'verify'],
  )
  const exported = await crypto.subtle.exportKey('raw', key)
  sessionStorage.setItem(SK_ITEM, btoa(String.fromCharCode(...new Uint8Array(exported))))
  return key
}

/** Sign and store the share text. Must be called before any navigation. */
export async function storeSharePayload(text: string): Promise<void> {
  const key = await getOrCreateSessionKey()
  const exp = Date.now() + TTL_MS
  const msg = new TextEncoder().encode(`${text}\x00${exp}`)
  const sig = await crypto.subtle.sign('HMAC', key, msg)
  const sigB64 = btoa(String.fromCharCode(...new Uint8Array(sig)))
  sessionStorage.setItem(PT_ITEM, JSON.stringify({ text, exp, sig: sigB64 }))
}

/**
 * Verify, consume, and return the stored share text.
 * Returns null if missing, expired, or tampered.
 * Always removes the token to prevent replay.
 */
export async function consumeSharePayload(): Promise<string | null> {
  const raw = sessionStorage.getItem(PT_ITEM)
  sessionStorage.removeItem(PT_ITEM)
  if (!raw) return null
  try {
    const { text, exp, sig: sigB64 } = JSON.parse(raw) as {
      text: string
      exp: number
      sig: string
    }
    if (Date.now() > exp) return null
    const key = await getOrCreateSessionKey()
    const msg = new TextEncoder().encode(`${text}\x00${exp}`)
    const sig = Uint8Array.from(atob(sigB64), (c) => c.charCodeAt(0))
    const valid = await crypto.subtle.verify('HMAC', key, sig, msg)
    return valid ? text : null
  } catch {
    return null
  }
}
